import { useEffect, useState } from 'react';

import { PIPELINE_RUN_LIMIT_MAX, type PipelineSchedule } from '@clm/shared-types';

import { getPipelineSchedule, setPipelineSchedule } from '../../api/pipeline-runs';
import { PIPELINE_FORBIDDEN_COPY } from '../pipeline-status-chips';
import { runProblemHead, toRunProblem, type RunProblem } from './shared';

/**
 * The daily schedule — CLM-83 (D-83-4).
 *
 * Admin only (the parent hides it for an identity known not to be an admin;
 * the API's `pipeline:approve` gate decides). Save posts exactly
 * `{ enabled, hour_utc, batch_limit }`; the admin who saves becomes the
 * recorded approver of every run the schedule produces, and the card shows who
 * that is.
 */

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

export function ScheduleCard() {
  const [schedule, setSchedule] = useState<PipelineSchedule | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [hour, setHour] = useState(2);
  const [batchLimit, setBatchLimit] = useState('50');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<RunProblem | null>(null);
  const [saved, setSaved] = useState(false);

  const apply = (next: PipelineSchedule) => {
    setSchedule(next);
    setEnabled(next.enabled);
    setHour(next.hour_utc);
    setBatchLimit(String(next.batch_limit));
  };

  useEffect(() => {
    const controller = new AbortController();
    void getPipelineSchedule(controller.signal).then(
      (response) => {
        if (!controller.signal.aborted) apply(response.schedule);
      },
      (error: unknown) => {
        if (!controller.signal.aborted) setProblem(toRunProblem(error));
      },
    );
    return () => controller.abort();
  }, []);

  const save = async () => {
    setBusy(true);
    setProblem(null);
    setSaved(false);
    try {
      const parsed = Number.parseInt(batchLimit, 10);
      const response = await setPipelineSchedule({ enabled, hour_utc: hour, batch_limit: Number.isFinite(parsed) ? parsed : 50 });
      apply(response.schedule);
      setSaved(true);
    } catch (error) {
      setProblem(toRunProblem(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" role="group" aria-label="Daily schedule">
      <h4>Schedule</h4>
      <label className="check">
        <input
          type="checkbox"
          aria-label="Schedule enabled"
          checked={enabled}
          disabled={busy || schedule === null}
          onChange={(event) => setEnabled(event.target.checked)}
        />{' '}
        Run daily
      </label>
      <div className="field">
        <label htmlFor="schedule-hour">Hour (UTC)</label>
        <select id="schedule-hour" value={hour} disabled={busy || schedule === null} onChange={(event) => setHour(Number(event.target.value))}>
          {HOURS.map((h) => (
            <option key={h} value={h}>
              {String(h).padStart(2, '0')}:00
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label htmlFor="schedule-limit">Batch limit (1–{PIPELINE_RUN_LIMIT_MAX})</label>
        <input
          id="schedule-limit"
          type="number"
          min={1}
          max={PIPELINE_RUN_LIMIT_MAX}
          value={batchLimit}
          disabled={busy || schedule === null}
          onChange={(event) => setBatchLimit(event.target.value)}
        />
      </div>
      <div className="vacts">
        <button type="button" className="btn pri" disabled={busy || schedule === null} onClick={() => void save()}>
          Save schedule
        </button>
      </div>
      {schedule === null ? null : (
        <p className="mut">
          {schedule.updated_by_upn === null
            ? 'Never changed — the seeded default (disabled, 02:00 UTC, batch 50).'
            : <>Last saved by {schedule.updated_by_upn} <time dateTime={schedule.updated_utc ?? ''}>{schedule.updated_utc}</time> — the recorded approver of every scheduled run.</>}
        </p>
      )}
      {saved ? (
        <p role="status" className="mut">
          Schedule saved.
        </p>
      ) : null}
      {problem === null ? null : (
        <div className="banner up" role="status">
          <p className="notice-head">
            <strong>{runProblemHead(problem, PIPELINE_FORBIDDEN_COPY)}</strong>
          </p>
          {problem.field_errors.map((entry) => (
            <p key={entry.path} className="mut">
              {entry.message}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}
